<template>
  <div id="app">
    <router-view />
    <Toast position="top-center" />
    <ConfirmDialog :draggable="false" dismissable-mask>
      <template #icon>
        <VdIcon store="far" name="circle-exclamation" size="large" color="var(--vd-color-text-light-1)"></VdIcon>
      </template>
    </ConfirmDialog>
  </div>
</template>

<script lang="ts">
import { useColorMode, useOnline, useStorage } from "@vueuse/core";
import { ElLoading } from "element-plus";
import { defineComponent, provide, watch } from "vue";

import VdIcon from "@/components/VdIcon/VdIcon.vue";
import { DEVELOPER_MODE, LAST_NOTIFICATION_ID_SEEN } from "@/constants";
import useToast from "@/hooks/useToastNotification";
import ConfirmDialog from "primevue/confirmdialog";
import Toast from "primevue/toast";
import { useToast as usePrimeToast } from "primevue/usetoast";
import useDeveloperMode from "@/hooks/useDeveloperMode";

/**
 * This is the actual vue application.
 */
export default defineComponent({
  name: "App",
  components: {
    Toast,
    ConfirmDialog,
    VdIcon,
  },

  setup() {
    const colorScheme = useColorMode({
      selector: "body",
      attribute: "data-color-scheme",
    });

    colorScheme.value = "light";

    const { isDeveloperMode } = useDeveloperMode();

    /**
     Monitor the user internet connection.
     If the connection breaks, pause the application and display a message.
    */
    const isOnline = useOnline();
    let loadingInstance: ReturnType<typeof ElLoading.service> | undefined;
    watch(isOnline, (isUserOnline) => {
      if (isUserOnline) {
        if (loadingInstance) {
          loadingInstance.close();
          loadingInstance = undefined;
        }
      } else {
        loadingInstance = ElLoading.service({
          lock: true,
          text: "Internet lost. Please check your connection...",
        });
      }
    });

    const lastNotificationIdSeen = useStorage<string>(LAST_NOTIFICATION_ID_SEEN, "");
    provide(LAST_NOTIFICATION_ID_SEEN, lastNotificationIdSeen);

    const toast = usePrimeToast();
    const { messenger: toastMessenger } = useToast();

    watch(toastMessenger, (notification) => {
      if (notification !== null) {
        toast.add({
          closable: notification.closable,
          detail: notification.detail,
          life: notification.life,
          severity: notification.severity,
          summary: notification.summary,
        });
      }
    });

    provide(DEVELOPER_MODE, isDeveloperMode);
  },
});
</script>
